// oxlint-disable-next-line cf-web/banned-imports
import { NoResultIcon } from 'primitives/icons/social'
import { useHits, useInstantSearch } from 'react-instantsearch'
import PhotoAlbum from 'react-photo-album'
import * as Avatar from 'primitives/avatar'
import * as React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { tw } from 'utils/classnames'

import type { AlgoliaTemplateHit } from '../context/templates-page'
import { useTemplatesPageContext } from '../context/templates-page'
import { getUrlFromTitleId, sanitizeTitle } from './utils'
import { HandleLocalStorage } from './handle-local-storage'
import { ClientOnly } from 'components/client-only'

const MAX_IMAGE_WIDTH = 300

export function SearchResults({ noResultsText }: { noResultsText: string }) {
  const { hits } = useHits<AlgoliaTemplateHit>()
  const { navigateToTemplate } = useTemplatesPageContext()
  const { status } = useInstantSearch()
  const { templatesItemRoute, itemPageSlug, isClubhouse } = useTemplatesPageContext()

  const deferredHits = React.useDeferredValue(hits)
  const filteredHits = deferredHits.filter(
    hit =>
      hit.imageUrl.includes('creativefabrica') &&
      // No point showing the current template in the similar results
      getUrlFromTitleId(hit.title, hit.objectID) !== itemPageSlug,
  )

  const noResultsTextWithClubhouse = isClubhouse ? (
    <NoResultsClubhouse />
  ) : (
    <div className="flex h-full items-center justify-center">
      <span className="text-14 text-gray-600">{noResultsText}</span>
    </div>
  )

  return (
    <div data-testid="search-results-container" className="min-h-[420px]">
      {filteredHits.length === 0 && status === 'idle' ? (
        noResultsTextWithClubhouse
      ) : (
        <>
          <ErrorBoundary fallback={null}>
            <ClientOnly fallback={null}>
              <HandleLocalStorage />
            </ClientOnly>
          </ErrorBoundary>
          <PhotoAlbum
            layout="masonry"
            photos={filteredHits.map(hit => ({
              href: `${templatesItemRoute}${getUrlFromTitleId(hit.title, hit.objectID)}/`,
              title: hit.title ? sanitizeTitle(hit.title) : '',
              src: hit.imageUrl,
              alt: hit.subject,
              width: MAX_IMAGE_WIDTH,
              height: MAX_IMAGE_WIDTH * (hit.height / hit.width),
              key: hit.objectID,
            }))}
            onClick={({ index }) => navigateToTemplate(filteredHits[index], filteredHits)}
            defaultContainerWidth={1200}
            columns={containerWidth => {
              if (containerWidth < 640) {
                return 2
              }
              if (containerWidth < 1024) {
                return 3
              }
              if (containerWidth < 1280) {
                return 4
              }
              if (containerWidth < 1536) {
                return 5
              }
              return 6
            }}
            renderPhoto={({
              photo: { href, title, src, alt, width, height },
              imageProps: { onClick },
            }) => (
              <TemplateItem
                href={href}
                title={title}
                imgProps={{ src, alt, width, height }}
                onClick={onClick}
              />
            )}
            renderColumnContainer={({ columnContainerProps: { style, ...rest }, children }) => (
              <div {...rest} style={{ ...style, gap: '32px', justifyContent: '' }}>
                {children}
              </div>
            )}
          />
        </>
      )}
    </div>
  )
}

interface TemplateItemProps {
  title: string
  imgProps: Omit<React.ComponentProps<'img'>, 'onClick' | 'className'>
  href?: string
  onClick?: React.MouseEventHandler<HTMLElement>
}

function TemplateItem({ title, onClick, imgProps, href }: TemplateItemProps) {
  const { isClubhouse } = useTemplatesPageContext()

  return (
    // Since we are intercepting the click to open the modal, we don't need to use the Link component
    <a
      href={href}
      className={tw(
        'group flex w-full flex-col gap-2',
        isClubhouse
          ? ''
          : 'transition-transform hover:scale-[1.02] focus:outline-none focus-visible:outline',
      )}
      onClick={e => e.preventDefault()}
    >
      <img
        {...imgProps}
        onClick={onClick}
        className={tw(
          'rounded-2 w-full border bg-gray-700',
          isClubhouse
            ? 'border-[#DADCE2] group-hover:border-[#1313151F] group-hover:shadow-[0px_4px_4px_0px_#00000040] active:border-[#0A0A0B1A] active:shadow-none'
            : 'border-[rgba(0,0,0,0.07)]',
        )}
      />

      <div className="mt-auto flex items-center">
        {!isClubhouse ? (
          <Avatar.Root className="block basis-0">
            <Avatar.Fallback delayMs={600}>CF</Avatar.Fallback>
          </Avatar.Root>
        ) : null}
        <div className={tw('overflow-hidden ', isClubhouse ? 'pl-0' : 'pl-2')}>
          <p
            className={tw(
              'text-14 text-gray-300-600 truncate',
              isClubhouse ? 'text-cf-grey-1000 font-semibold' : '',
            )}
          >
            {title}
          </p>
          {!isClubhouse ? <p className="text-gray-400-400 text-xs">by Creative Fabrica</p> : null}
        </div>
      </div>
    </a>
  )
}

function NoResultsClubhouse() {
  return (
    <div className="flex flex-col items-center justify-center px-8 pt-10">
      <NoResultIcon className="mb-2" />
      <h2 className="mb-2.5 w-full text-center text-[18px] font-bold leading-[26px]">
        No templates found
      </h2>
      <p className="text-16 text-center leading-6">Try searching for something else.</p>
    </div>
  )
}
